import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Edit, Plus, Trash2, Building2 } from 'lucide-react';
import AddPatientModal from '../components/AddPatientModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employerFilter, setEmployerFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients, statusFilter, employerFilter]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatients(response.data);
      setFilteredPatients(response.data);
    } catch (error) {
      // console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };


  // Helper functions to handle both old and new name formats
  const getFullName = (identifying_data) => {
    if (!identifying_data) return 'Unknown';
    const first = identifying_data.first_name || '';
    const middle = identifying_data.middle_name || '';
    const last = identifying_data.last_name || '';
    const newFormat = `${first} ${middle} ${last}`.trim();
    return newFormat || identifying_data.full_name || 'Unknown';
  };

  const getDisplayName = (identifying_data) => {
    if (!identifying_data) return 'Unknown';
    const first = identifying_data.first_name || '';
    const last = identifying_data.last_name || '';
    const newFormat = `${first} ${last}`.trim();
    return newFormat || identifying_data.full_name || 'Unknown';
  };

  const filterPatients = () => {
    let filtered = patients.filter(patient => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = getFullName(patient.identifying_data).toLowerCase();
      const matchesSearch = (
        fullName.includes(searchLower) ||
        patient.identifying_data?.emergency_contact_phone?.toLowerCase().includes(searchLower) ||
        patient.id?.toLowerCase().includes(searchLower)
      );

      const matchesStatus = statusFilter === 'all' || patient.membership_status?.status === statusFilter;
      
      const matchesEmployer = employerFilter === 'all' ||
        (employerFilter === 'none' && !patient.employer_id) ||
        patient.employer_id === employerFilter;

      return matchesSearch && matchesStatus && matchesEmployer;
    });

    setFilteredPatients(filtered);
  };

  // Get unique employers from patient list
  const employerOptions = React.useMemo(() => {
    const map = new Map();
    patients.forEach(p => {
      if (p.employer_id && p.employer_name) {
        map.set(p.employer_id, p.employer_name);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [patients]);

  const handleDeletePatient = async (patientId, patientName) => {
    if (!window.confirm(`Are you sure you want to delete ${patientName}?\n\nThis action cannot be undone and will permanently remove:\n- Patient account\n- All medical records\n- Health history\n- Care team assignments\n\nType DELETE to confirm:`)) {
      return;
    }

    const confirmText = window.prompt(`Type "DELETE" to confirm deletion of ${patientName}:`);
    if (confirmText !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      await axios.delete(`${API}/admin/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`${patientName} has been deleted successfully`);
      fetchPatients();
    } catch (error) {
      // console.error('Error deleting patient:', error);
      toast.error('Failed to delete patient account');
    }
  };

  const getMembershipBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="badge-active">Active</Badge>;
      case 'inactive':
        return <Badge className="badge-inactive">Inactive</Badge>;
      case 'expiring_soon':
        return <Badge className="badge-expiring">Expiring</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatLocation = (location) => {
    if (!location) return '';
    // Shorten NCR city names
    if (location === 'Quezon City') return 'Q.C.';
    // Remove "City" from other NCR cities
    return location.replace(' City', '');
  };

  const calculateMembershipLength = (startDate, expiryDate) => {
    const start = new Date(startDate);
    const expiry = new Date(expiryDate);
    
    const yearDiff = expiry.getFullYear() - start.getFullYear();
    const monthDiff = expiry.getMonth() - start.getMonth();
    const dayDiff = expiry.getDate() - start.getDate();
    
    let totalMonths = yearDiff * 12 + monthDiff;
    
    // Round up if there's any day difference
    if (dayDiff > 0) {
      totalMonths += 1;
    }
    
    return totalMonths;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  };

  const calculateAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading census...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Left Wave Decoration */}
      <div className="absolute left-0 top-0 bottom-0 w-1/4 pointer-events-none opacity-30">
        <svg viewBox="0 0 400 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M0 0C150 200 100 400 200 540C300 680 250 880 150 1080H0V0Z" fill="url(#leftGradient)" />
          <defs>
            <linearGradient id="leftGradient" x1="0" y1="0" x2="200" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0EA5E9" />
              <stop offset="1" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Right Wave Decoration */}
      <div className="absolute right-0 top-0 bottom-0 w-1/3 pointer-events-none opacity-30">
        <svg viewBox="0 0 600 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M600 0C450 150 500 350 350 540C200 730 300 900 450 1080H600V0Z" fill="url(#rightGradient)" />
          <defs>
            <linearGradient id="rightGradient" x1="600" y1="0" x2="300" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0284C7" />
              <stop offset="1" stopColor="#0EA5E9" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8" data-testid="admin-dashboard-header">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Patient Census</h1>
          <p className="text-lg text-slate-600">Manage membership and patient information</p>
        </div>

        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Search by name, phone, or ID..."
                data-testid="census-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="status-filter-select"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expiring_soon">Expiring Soon</option>
              </select>
              <select
                value={employerFilter}
                onChange={(e) => setEmployerFilter(e.target.value)}
                data-testid="employer-filter-select"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <option value="all">All Employers</option>
                <option value="none">Self-enrolled (No Employer)</option>
                {employerOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <Button
                onClick={() => setShowAddModal(true)}
                data-testid="add-patient-button"
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                Add Patient
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="census-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">#</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Location</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Age</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Sex</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Employer</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Start Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Length</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-8 text-slate-500">
                      {employerFilter !== 'all'
                        ? 'No patients found for the selected employer'
                        : 'No patients found'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredPatients.map((patient, index) => (
                    <tr key={patient.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-4 text-center text-slate-600 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          {patient.identifying_data.profile_photo ? (
                            <img
                              src={patient.identifying_data.profile_photo}
                              alt={getDisplayName(patient.identifying_data)}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                              <span className="text-xl font-bold text-cyan-700">
                                {getDisplayName(patient.identifying_data).charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{getDisplayName(patient.identifying_data)}</p>
                            <p className="text-xs text-slate-500 font-mono">ID: {patient.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-slate-700 text-sm whitespace-nowrap">
                        {patient.identifying_data.location ? (
                          <span className="font-medium">{formatLocation(patient.identifying_data.location)}</span>
                        ) : (
                          <span className="text-slate-400 italic">Not set</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-slate-700">{calculateAge(patient.identifying_data.birthdate)}</td>
                      <td className="py-4 px-4 text-slate-700">{patient.identifying_data.sex}</td>
                      <td className="py-4 px-4">{getMembershipBadge(patient.membership_status.status)}</td>
                      <td className="hidden lg:table-cell py-4 px-4 text-slate-700 text-sm whitespace-nowrap">
                        {patient.employer_name ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={14} className="text-teal-600" />
                            <span className="font-medium">{patient.employer_name}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">Self-enrolled</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-slate-700 text-sm whitespace-nowrap">{formatDate(patient.membership_status.start_date)}</td>
                      <td className="py-4 px-4 text-slate-700 text-sm font-medium whitespace-nowrap">
                        {calculateMembershipLength(patient.membership_status.start_date, patient.membership_status.expiry_date)} months
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => navigate(`/admin/patient/${patient.id}/edit`)}
                            data-testid={`edit-patient-${patient.id}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDeletePatient(patient.id, getDisplayName(patient.identifying_data))}
                            data-testid={`delete-patient-${patient.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Total Patients</h3>
            <p className="text-3xl font-bold text-slate-900">{patients.length}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Active Members</h3>
            <p className="text-3xl font-bold text-green-600">
              {patients.filter(p => p.membership_status.status === 'active').length}
            </p>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Expiring Soon</h3>
            <p className="text-3xl font-bold text-orange-600">
              {patients.filter(p => p.membership_status.status === 'expiring_soon').length}
            </p>
          </div>
        </div>
      </div>

      <AddPatientModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchPatients();
          setShowAddModal(false);
        }}
      />
    </div>
  );
};

export default AdminDashboard;